import React, { useState, useEffect, useRef } from 'react';
import { Mic, Square, ArrowLeft, Copy, Send, X } from 'lucide-react';
import { openaiRealtimeService } from '../services/openaiRealtimeService';

const TranscribeMode = ({ onClose, onBack, onTranscript }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [status, setStatus] = useState('idle'); // idle, initializing, ready, listening, processing
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    // Initialize transcribe mode
    const initialize = async () => {
      try {
        const success = await openaiRealtimeService.initialize('transcribe', {
          onTranscript: handleTranscript,
          onStatusChange: setStatus,
          onError: setError
        });
        
        if (success) {
          setIsConnected(true);
        }
      } catch (error) {
        console.error('Failed to initialize transcribe mode:', error);
        setError(error);
      }
    };

    initialize();

    // Cleanup on unmount
    return () => {
      openaiRealtimeService.cleanup();
    };
  }, []);

  const handleTranscript = (text) => {
    setTranscript(prev => prev + (prev ? ' ' : '') + text);
    
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  const startRecording = () => {
    if (!isConnected) {
      setError(new Error('Not connected to service'));
      return;
    }
    
    console.log('🎤 Starting transcription recording...');
    openaiRealtimeService.startRecording();
    setIsRecording(true);
  };

  const stopRecording = () => {
    console.log('🛑 Stopping transcription recording...');
    openaiRealtimeService.stopRecording();
    setIsRecording(false);
  };

  const clearTranscript = () => {
    setTranscript('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const copyTranscript = async () => {
    try {
      await navigator.clipboard.writeText(transcript);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy transcript:', err);
    }
  };

  const sendTranscript = () => {
    if (transcript.trim() && onTranscript) {
      onTranscript(transcript.trim());
      clearTranscript();
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'listening': return 'text-blue-600';
      case 'processing': return 'text-orange-600';
      case 'ready': return 'text-green-600';
      case 'initializing': return 'text-purple-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'listening': return 'Listening to your speech...';
      case 'processing': return 'Converting speech to text...';
      case 'ready': return 'Ready - click microphone to start';
      case 'initializing': return 'Setting up speech recognition...';
      default: return 'Click to start';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl h-[600px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mic className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Transcribe Mode</h2>
                <p className="text-sm text-gray-600">Speech to text</p>
              </div>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Status Bar */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${
                isConnected ? 'bg-green-500' : 'bg-red-500'
              }`} />
              <span className={`text-sm font-medium ${getStatusColor()}`}>
                {getStatusMessage()}
              </span>
            </div>
            
            {/* Connection indicator */}
            <div className="text-xs text-gray-500">
              {isConnected ? 'Connected' : 'Connecting...'}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          {/* Recording Controls */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={!isConnected}
                className={`w-20 h-20 rounded-full border-4 flex items-center justify-center transition-all ${
                  isRecording
                    ? 'bg-red-500 border-red-600 text-white animate-pulse'
                    : isConnected
                    ? 'bg-blue-500 border-blue-600 text-white hover:bg-blue-600'
                    : 'bg-gray-300 border-gray-400 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isRecording ? <Square className="w-8 h-8" /> : <Mic className="w-8 h-8" />}
              </button>
            </div>
            
            <p className="text-sm text-gray-600">
              {isRecording ? 'Click to stop recording' : 'Click to start recording'}
            </p>
          </div>

          {/* Transcript Output */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Transcribed Text
              </label>
              <div className="flex gap-2">
                {transcript && (
                  <>
                    <button
                      onClick={copyTranscript}
                      className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                      title="Copy transcript"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={clearTranscript}
                      className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      Clear
                    </button>
                  </>
                )}
              </div>
            </div>
            
            <textarea
              ref={textareaRef}
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              placeholder="Your speech will appear here as text..."
              className="w-full min-h-32 max-h-48 p-4 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              style={{ height: 'auto' }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500">
              Edit the transcribed text above before sending
            </div>
            
            {onTranscript && transcript.trim() && (
              <button
                onClick={sendTranscript}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <Send className="w-4 h-4" />
                Send Message
              </button>
            )}
          </div>
          
          {error && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 font-medium">Error</p>
              <p className="text-sm text-red-500">{error.message}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TranscribeMode;